import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import api from "../api/Api.js";
// Using Lucide icons for a premium look
import { Search, Plus, Edit2, Check, X } from "lucide-react"; 
import "bootstrap/dist/css/bootstrap.min.css"; // Ensure Bootstrap is imported
// import "./ConfirmationhecklistPage.css"; 

const ConfirmationhecklistPage = () => {
  const [masterItems, setMasterItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);

  // Edit State
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editShortName, setEditShortName] = useState("");
  // ✅ New Edit States
  const [editPhoto, setEditPhoto] = useState(0); 
  const [editMandatory, setEditMandatory] = useState(0);

  const location = useLocation();
  // para 1 = Vehicle Checklist, para 2 = Salary Checklist
  const para = location.pathname.includes("salary-ddt-checklist") ? 2 : 1;

  useEffect(() => {
    fetchItemsDetails();
  }, [para]);

  // Search Filter Logic
  useEffect(() => {
    if (!searchQuery) {
      setFilteredItems(masterItems);
    } else {
      const lowerQuery = searchQuery.toLowerCase();
      const filtered = masterItems.filter((item) =>
        item.item_names.toLowerCase().includes(lowerQuery) ||
        (item.short_name && item.short_name.toLowerCase().includes(lowerQuery))
      );
      setFilteredItems(filtered);
    }
  }, [searchQuery, masterItems]);

  const fetchItemsDetails = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/adminmod/getMastadata/${para}`);
      const data = res.data?.data || [];
      setMasterItems(data);
      setFilteredItems(data);
    } catch {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id, value) => {
    try {
      // Optimistic UI Update
      const updatedList = masterItems.map((item) => 
        item.id === id ? { ...item, is_active: parseInt(value) } : item
      );
      setMasterItems(updatedList);
      setFilteredItems(updatedList);

      await api.put("/adminmod/items-status", { id, is_active: value });
      toast.success("Status updated");
    } catch {
      toast.error("Failed to update status");
      fetchItemsDetails(); // Revert on error
    }
  };

  const handleEdit = (item) => {
    setEditId(item.id);
    setEditName(item.item_names);
    setEditShortName(item.short_name || "");
    // ✅ Load existing values
    setEditPhoto(item.photo || 0);
    setEditMandatory(item.is_mandatory || 0);
  };

  const cancelEdit = () => {
    setEditId(null);
    setEditName("");
    setEditShortName("");
    setEditPhoto(0);
    setEditMandatory(0);
  };

  const handleEditSave = async () => {
    if (!editName.trim()) {
      toast.error("Name is required");
      return;
    }

    try {
      await api.put("/adminmod/items-edit", {
        id: editId,
        item_names: editName,
        short_name: para === 2 ? editShortName : null,
        photo: para === 1 ? editPhoto : 0,           // Only save photo for Vehicle
        is_mandatory: para === 1 ? editMandatory : 0, // Only save mandatory for Vehicle
        para,
      });

      toast.success("Item updated successfully");
      cancelEdit();
      fetchItemsDetails();
    } catch {
      toast.error("Failed to update item");
    }
  };

  const pageTitle = para === 1 ? "Vehicle Checklist Items" : "Salary Deduction Components";

  return (
    <div className="container-fluid py-3 bg-white min-vh-100">
      
      {/* HEADER CARD */}
      <div className="card shadow-sm border-0 bg-primary-subtle mb-4 rounded-3">
        <div className="card-body d-flex flex-wrap align-items-center justify-content-between gap-3 p-3">
          <div>
            <h4 className="mb-1 fw-bold text-dark">{pageTitle}</h4>
            <p className="text-muted small mb-0">Manage your master data efficiently</p>
          </div>
          
          <div className="d-flex gap-2">
            {/* Search Input */}
            <div className="input-group input-group-sm" style={{ maxWidth: "250px" }}>
              <span className="input-group-text bg-white border-end-0">
                <Search size={16} className="text-muted" />
              </span>
              <input
                type="text"
                className="form-control border-start-0 ps-0"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            {/* Add Button */}
            <Link
              to={para === 1 ? "/master/confirmation-checklist/add" : "/master/salary-ddt-checklist/add"}
              className="btn btn-primary btn-sm d-flex align-items-center gap-2 fw-medium px-3"
            >
              <Plus size={16} /> Add New
            </Link>
          </div>
        </div>
      </div>

      {/* TABLE CARD */}
      <div className="card shadow-sm border-0 rounded-3 overflow-hidden">
        <div className="table-responsive">
          {/* ✅ Added table-sm for compactness, table-hover for UX */}
          <table className="table table-hover table-sm align-middle mb-0">
            <thead className="bg-light">
              <tr>
                <th className="text-center py-3 text-secondary text-uppercase small fw-bold" style={{ width: "5%" }}>#</th>
                <th className="py-3 text-secondary text-uppercase small fw-bold">Item Name</th>
                {para === 2 && <th className="py-3 text-secondary text-uppercase small fw-bold" style={{ width: "15%" }}>Short Code</th>}
                
                {/* ✅ Photo Column (Only for Vehicle) */}
                {para === 1 && <th className="py-3 text-center text-secondary text-uppercase small fw-bold" style={{ width: "10%" }}>Photo</th>}
                
                {/* ✅ Mandatory Column (Only for Vehicle) */}
                {para === 1 && <th className="py-3 text-center text-secondary text-uppercase small fw-bold" style={{ width: "10%" }}>Mandatory</th>}
                
                <th className="py-3 text-center text-secondary text-uppercase small fw-bold" style={{ width: "15%" }}>Status</th>
                <th className="py-3 text-center text-secondary text-uppercase small fw-bold" style={{ width: "10%" }}>Actions</th>
              </tr>
            </thead>
            
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="10" className="text-center py-5 text-muted">
                    <div className="spinner-border spinner-border-sm text-primary me-2" role="status"></div>
                    Loading data...
                  </td>
                </tr>
              ) : filteredItems.length === 0 ? (
                <tr>
                  <td colSpan="10" className="text-center py-5 text-muted">No items found.</td>
                </tr>
              ) : (
                filteredItems.map((item, index) => (
                  <tr key={item.id} className={editId === item.id ? "table-active" : ""}>
                    <td className="text-center text-muted small">{index + 1}</td>

                    {/* ITEM NAME */}
                    <td>
                      {editId === item.id ? (
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          autoFocus
                        />
                      ) : (
                        <span className="fw-medium text-dark">{item.item_names}</span>
                      )}
                    </td>

                    {/* SHORT CODE (Salary Only) */}
                    {para === 2 && (
                      <td>
                        {editId === item.id ? (
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={editShortName}
                            onChange={(e) => setEditShortName(e.target.value)}
                          />
                        ) : (
                          <span className="badge bg-info-subtle text-dark border">{item.short_name || "-"}</span>
                        )}
                      </td>
                    )}

                    {/* ✅ PHOTO PROOF (Vehicle Only) */}
                    {para === 1 && (
                      <td className="text-center">
                        {editId === item.id ? (
                          <select
                            className="form-select form-select-sm"
                            value={editPhoto}
                            onChange={(e) => setEditPhoto(e.target.value)}
                          >
                            <option value="1">Yes</option>
                            <option value="0">No</option>
                          </select>
                        ) : (
                          <span className={`badge ${item.photo == 1 ? "bg-info-subtle text-info" : "bg-secondary-subtle text-secondary"}`}>
                            {item.photo == 1 ? "Yes" : "No"}
                          </span>
                        )}
                      </td>
                    )}

                    {/* ✅ IS MANDATORY (Vehicle Only) */}
                    {para === 1 && (
                      <td className="text-center">
                        {editId === item.id ? (
                          <select
                            className="form-select form-select-sm"
                            value={editMandatory}
                            onChange={(e) => setEditMandatory(e.target.value)}
                          >
                            <option value="1">Yes</option>
                            <option value="0">No</option>
                          </select>
                        ) : (
                          <span className={`badge ${item.is_mandatory == 1 ? "bg-danger-subtle text-danger" : "bg-success-subtle text-success"}`}>
                            {item.is_mandatory == 1 ? "Required" : "Optional"}
                          </span>
                        )}
                      </td>
                    )}

                    {/* STATUS */}
                    <td className="text-center">
                      <select
                        className={`form-select form-select-sm border-0 fw-bold ${item.is_active == 1 ? "text-success bg-success-subtle" : "text-danger bg-danger-subtle"}`}
                        style={{ width: "100px", margin: "0 auto", cursor: "pointer" }}
                        value={item.is_active}
                        onChange={(e) => handleStatusChange(item.id, e.target.value)}
                        disabled={editId === item.id}
                      >
                        <option value="1">Active</option>
                        <option value="0">Inactive</option>
                      </select>
                    </td>

                    {/* ACTIONS */}
                    <td className="text-center">
                      {editId === item.id ? (
                        <div className="d-flex justify-content-center gap-1">
                          <button className="btn btn-success btn-sm p-1" onClick={handleEditSave} title="Save">
                            <Check size={16} />
                          </button>
                          <button className="btn btn-danger btn-sm p-1" onClick={cancelEdit} title="Cancel">
                            <X size={16} />
                          </button>
                        </div>
                      ) : (
                        <button 
                          className="btn btn-light btn-sm text-primary hover-shadow" 
                          onClick={() => handleEdit(item)} 
                          title="Edit"
                        >
                          <Edit2 size={16} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        <div className="card-footer bg-white py-3">
          <small className="text-muted">Showing {filteredItems.length} records</small>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationhecklistPage;